import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  ApplyButton,
  BulkFieldTextField,
  ErrorText,
  FieldsStack,
  InstructionText,
  SubtitleText,
} from "common/Components/BulkEditTagsDialog/BulkEditTagsDialog.styles";
import ParentNodeTreePicker from "common/Components/ParentNodeTreePicker/ParentNodeTreePicker";
import {
  DetailsReadOnlyStack,
  DrawerColumn,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderMain,
  DrawerScrollBody,
  DrawerSelectedText,
  DrawerTitle,
  EditableFieldsHeading,
  ReadOnlyFieldCopyButton,
  ReadOnlyFieldMain,
  ReadOnlyFieldRow,
  ReadOnlyFieldValue,
  StyledDrawer,
} from "./TagDetailsDrawer.styles";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";
import {
  NODE_NAME_MAX_LENGTH,
  NODE_NAME_MESSAGES,
  nodeNameFormatError,
  validateNodeNameForSave,
} from "common/utils/nodeNameValidation";
import { filterNodeTypeOptionsForTagEdit } from "./tagRowsMapper";
import { fetchContextNodeForTagRow } from "./tagsApi";

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

const selectInputLabelShrink = { shrink: true };

function getTagNodeTypeOption(nodeTypeOptions) {
  const fromReference = (nodeTypeOptions ?? []).find(
    (option) => String(option?.value ?? "").toLowerCase() === "tag"
  );
  return fromReference ?? { value: "tag", label: "Tag" };
}

function createEmptyForm() {
  return {
    nodeType: "",
    nodeSubType: "",
    parentNode: "",
    parentNodeId: null,
    parentNodeName: "",
    nodeName: "",
  };
}

function formFromTagsRow(row) {
  return {
    nodeType: String(row.nodeType ?? ""),
    nodeSubType: String(row.nodeSubType ?? ""),
    parentNode:
      row.parentNode != null && row.parentNode !== ""
        ? String(row.parentNode)
        : "",
    parentNodeId: isFiniteNumber(row.parentNodeId)
      ? Number(row.parentNodeId)
      : null,
    parentNodeName:
      row.parentNodeName != null && String(row.parentNodeName).trim() !== ""
        ? String(row.parentNodeName).trim()
        : "",
    nodeName: row.nodeName != null ? String(row.nodeName) : "",
  };
}

function formFromContextNodeDto(dto, row) {
  const parentNodeId = isFiniteNumber(dto?.parentNodeId)
    ? Number(dto.parentNodeId)
    : null;
  return {
    nodeType: String(dto?.nodeType ?? row?.nodeType ?? ""),
    nodeSubType: String(
      dto?.nodeSubType ?? dto?.nodeSubtype ?? row?.nodeSubType ?? ""
    ),
    parentNode: parentNodeId != null ? String(parentNodeId) : "",
    parentNodeId,
    parentNodeName:
      dto?.parentNodeName != null && String(dto.parentNodeName).trim() !== ""
        ? String(dto.parentNodeName).trim()
        : "",
    nodeName: String(dto?.nodeName ?? row?.nodeName ?? ""),
  };
}

function readOnlyRow({ label, value, onCopy }) {
  const display =
    isNullUndefinedOrEmptyString(value) ? "—" : String(value);
  return (
    <ReadOnlyFieldRow>
      <ReadOnlyFieldMain>
        <Typography variant="caption" color="text.secondary" component="div">
          {label}
        </Typography>
        <ReadOnlyFieldValue variant="body2" component="div" title={display}>
          {display}
        </ReadOnlyFieldValue>
      </ReadOnlyFieldMain>
      {display !== "—" ? (
        <ReadOnlyFieldCopyButton>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              onClick={() => onCopy(display)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ReadOnlyFieldCopyButton>
      ) : null}
    </ReadOnlyFieldRow>
  );
}

export default function TagDetailsDrawer({
  open,
  row,
  onClose,
  onSave,
  saving,
  nodeTypeOptions,
  subtypeOptionsByNodeType,
  referencePayload,
  parentCandidates,
}) {
  const [form, setForm] = useState(createEmptyForm);
  const [validationError, setValidationError] = useState("");
  const [resolvedContextNodeId, setResolvedContextNodeId] = useState(null);

  const isUncontextualized = Boolean(row?.uncontextualized);
  const contextualizeNodeTypeOption = useMemo(
    () => getTagNodeTypeOption(nodeTypeOptions),
    [nodeTypeOptions]
  );

  const subtypeSelectOptions = useMemo(
    () =>
      form.nodeType
        ? subtypeOptionsByNodeType[String(form.nodeType)] ?? []
        : [],
    [form.nodeType, subtypeOptionsByNodeType]
  );

  const editableNodeTypeOptions = useMemo(
    () => filterNodeTypeOptionsForTagEdit(nodeTypeOptions),
    [nodeTypeOptions]
  );

  useEffect(() => {
    if (!open || !row) {
      setForm(createEmptyForm());
      setValidationError("");
      setResolvedContextNodeId(null);
      return;
    }
    if (row.uncontextualized) {
      setForm({
        nodeType: String(contextualizeNodeTypeOption.value),
        nodeSubType: "",
        parentNode: "",
        parentNodeId: null,
        parentNodeName: "",
        nodeName: row.nodeName != null ? String(row.nodeName) : "",
      });
      setValidationError("");
      setResolvedContextNodeId(null);
      return;
    }

    setForm(formFromTagsRow(row));
    setValidationError("");
    setResolvedContextNodeId(null);

    let cancelled = false;
    (async () => {
      const result = await fetchContextNodeForTagRow(row);
      if (cancelled || result?.error || !result?.data) {
        return;
      }
      const nodeId = result.data.nodeId;
      if (isFiniteNumber(nodeId)) {
        setResolvedContextNodeId(Number(nodeId));
      }
      setForm(formFromContextNodeDto(result.data, row));
    })();

    return () => {
      cancelled = true;
    };
  }, [open, row, contextualizeNodeTypeOption.value]);

  const mergeForm = (patch) => setForm((previous) => ({ ...previous, ...patch }));

  const handleCopy = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  const handleSave = async () => {
    setValidationError("");
    if (!row) return;

    if (isUncontextualized) {
      const nodeName = (form.nodeName ?? "").trim();
      const nodeNameError = validateNodeNameForSave(form.nodeName);
      if (nodeNameError) {
        setValidationError(nodeNameError);
        return;
      }
      const patch = {
        nodeType: String(contextualizeNodeTypeOption.value),
        nodeName,
      };
      if ((form.nodeSubType ?? "").trim()) {
        patch.nodeSubType = form.nodeSubType.trim();
      }
      if (form.parentNodeId != null && form.parentNodeId !== "") {
        patch.parentNodeId = form.parentNodeId;
      }
      await onSave?.(patch);
      return;
    }

    if (!(form.nodeType ?? "").trim()) {
      setValidationError("Select a node type.");
      return;
    }
    const nodeNameError = validateNodeNameForSave(form.nodeName);
    if (nodeNameError) {
      setValidationError(nodeNameError);
      return;
    }
    const patch = {
      nodeType: form.nodeType.trim(),
      nodeName: form.nodeName.trim(),
    };
    if ((form.nodeSubType ?? "").trim()) {
      patch.nodeSubType = form.nodeSubType.trim();
    }
    if (form.parentNodeId != null && form.parentNodeId !== "") {
      patch.parentNodeId = form.parentNodeId;
    }
    await onSave?.(patch, resolvedContextNodeId);
  };

  const selectedLabel = row
    ? row.edgeTag || row.canonicalTagId || row.nodeName || row.rowKey || ""
    : "";

  const sourceNameDisplay =
    row?.sourceConnector ?? row?.sourceSystem ?? "";

  const nodeNameLiveError =
    nodeNameFormatError(form.nodeName) ||
    (form.nodeName.length >= NODE_NAME_MAX_LENGTH
      ? NODE_NAME_MESSAGES.MAX_LENGTH
      : "");

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <DrawerColumn>
        <DrawerHeader>
          <DrawerHeaderMain>
            <DrawerTitle variant="h6" component="div">
              Tag Details
            </DrawerTitle>
            {row ? (
              <DrawerSelectedText variant="body2" color="text.secondary">
                Selected: {selectedLabel}
              </DrawerSelectedText>
            ) : null}
          </DrawerHeaderMain>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <DrawerScrollBody>
          {row ? (
            <>
              <SubtitleText variant="subtitle2" component="div">
                Details
              </SubtitleText>
              <DetailsReadOnlyStack>
                {readOnlyRow({
                  label: "Source Connector",
                  value: sourceNameDisplay,
                  onCopy: handleCopy,
                })}
                {readOnlyRow({
                  label: "Source Path",
                  value: row.edgeTag ?? row.canonicalTagId,
                  onCopy: handleCopy,
                })}
                {readOnlyRow({
                  label: "Alias Path",
                  value: row.sourceTagId || row.context,
                  onCopy: handleCopy,
                })}
              </DetailsReadOnlyStack>

              <EditableFieldsHeading variant="subtitle2" component="div">
                Editable fields
              </EditableFieldsHeading>
              <InstructionText variant="body2" component="div">
                {isUncontextualized
                  ? "Set node name (required) and optional subtype and parent to contextualize this metric."
                  : "Update node type, subtype, parent, and name. Leave subtype empty if not applicable."}
              </InstructionText>

              {validationError ? (
                <ErrorText component="div">{validationError}</ErrorText>
              ) : null}

              <FieldsStack>
                <BulkFieldTextField
                  select
                  size="small"
                  label="Node Type"
                  disabled={isUncontextualized}
                  value={form.nodeType}
                  onChange={(e) =>
                    mergeForm({
                      nodeType: e.target.value,
                      nodeSubType: "",
                      parentNode: "",
                      parentNodeId: null,
                    })
                  }
                  InputLabelProps={selectInputLabelShrink}
                  InputProps={isUncontextualized ? { readOnly: true } : undefined}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                >
                  {(isUncontextualized
                    ? [contextualizeNodeTypeOption]
                    : editableNodeTypeOptions
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </BulkFieldTextField>

                <BulkFieldTextField
                  select
                  size="small"
                  label="Node Subtype"
                  disabled={!form.nodeType}
                  value={form.nodeSubType}
                  onChange={(e) => mergeForm({ nodeSubType: e.target.value })}
                  InputLabelProps={selectInputLabelShrink}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === "" || selected == null) {
                        return "";
                      }
                      const opt = subtypeSelectOptions.find(
                        (o) => String(o.value) === String(selected)
                      );
                      return opt?.label ?? String(selected);
                    },
                  }}
                >
                  {!form.nodeType ? (
                    <MenuItem value="" disabled>
                      <em>Select a node type</em>
                    </MenuItem>
                  ) : !subtypeSelectOptions.length ? (
                    <MenuItem value="" disabled>
                      <em>No subtypes for this type</em>
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem key="__none__" value="">
                        <em>None</em>
                      </MenuItem>,
                      ...subtypeSelectOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      )),
                    ]
                  )}
                </BulkFieldTextField>

                <ParentNodeTreePicker
                  label="Parent Node"
                  nodeType={form.nodeType}
                  referencePayload={referencePayload}
                  parentCandidates={parentCandidates}
                  disabled={!form.nodeType}
                  valueId={form.parentNodeId}
                  displayNameOverride={
                    form.parentNodeName != null &&
                    String(form.parentNodeName).trim() !== ""
                      ? String(form.parentNodeName).trim()
                      : undefined
                  }
                  onChange={(patch) => mergeForm(patch)}
                  inputTestId="tag-details-parent-picker"
                  placeholder="None"
                />

                <BulkFieldTextField
                  size="small"
                  label="Node Name"
                  value={form.nodeName}
                  onChange={(e) => {
                    mergeForm({ nodeName: e.target.value });
                    if (
                      validationError === NODE_NAME_MESSAGES.MAX_LENGTH ||
                      validationError === NODE_NAME_MESSAGES.NO_SPECIAL_CHARS ||
                      validationError === NODE_NAME_MESSAGES.REQUIRED
                    ) {
                      setValidationError("");
                    }
                  }}
                  fullWidth
                  error={Boolean(nodeNameLiveError)}
                  helperText={nodeNameLiveError || undefined}
                  inputProps={{ maxLength: NODE_NAME_MAX_LENGTH }}
                />
              </FieldsStack>
            </>
          ) : null}
        </DrawerScrollBody>

        <DrawerFooter>
          <ApplyButton
            fullWidth
            variant="contained"
            disabled={!row || saving}
            onClick={handleSave}
            data-testid="tag-details-save"
          >
            Save changes
          </ApplyButton>
        </DrawerFooter>
      </DrawerColumn>
    </StyledDrawer>
  );
}

TagDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  nodeTypeOptions: PropTypes.arrayOf(optionShape).isRequired,
  subtypeOptionsByNodeType: PropTypes.objectOf(PropTypes.arrayOf(optionShape)),
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
};

TagDetailsDrawer.defaultProps = {
  row: null,
  saving: false,
  subtypeOptionsByNodeType: {},
  referencePayload: null,
  parentCandidates: [],
};
